import { Flex } from "@chakra-ui/react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePapaParse } from "react-papaparse";
import { graphql, useFragment } from "react-relay";

import { AgeRangeReportTable_site$key } from "~/src/__relay_artifacts__/AgeRangeReportTable_site.graphql";
import { Table } from "~/src/components/common/tables/Table";

import { CSVDownloadLink } from "../CSVDownloadLink";
import { DisableColumnSelectButton } from "../DisableColumnSelectButton";
import {
  ColumnData,
  ReportTableDataRow,
  ReportTableHeaderRow,
} from "../ReportTable";
import { RowHeightSettingButton } from "../RowHeightSettingButton";
import { disableColumnOptions, headerOptionsForAgeRangeReport } from "../utils";

const fragment = graphql`
  fragment AgeRangeReportTable_site on Site {
    reportAgeRange(input: $input)
  }
`;

export type Props = {
  onSortClick: (params: any) => void;
  siteRef: AgeRangeReportTable_site$key;
};

export const AgeRangeReportTable: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteRef, onSortClick }) => {
  const { reportAgeRange } = useFragment<typeof siteRef>(fragment, siteRef);

  const [disableColumns, setDisableColumns] = useState<number[]>([]);
  const [rowHeight, setRowHeight] = useState<number>(56);

  const { readString } = usePapaParse();

  const [data, setData] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: [],
  });

  useEffect(() => {
    readString(reportAgeRange.summary_report, {
      worker: true,
      complete: (results: any) => {
        const [headers, ...rest] = results.data;
        setData({ headers, rows: rest });
      },
    });
  }, [readString, reportAgeRange]);

  const handleSubmitDisalbeColumnSelect = useCallback((values: any) => {
    setDisableColumns(values.disableColumns);
  }, []);

  const handleSubmitRowHeightSetting = useCallback((values: any) => {
    setRowHeight(values.rowHeight);
  }, []);

  const tableRows = useMemo(() => {
    return data.rows.map((row) => {
      return row.map((column) => {
        return {
          type: "TEXT",
          value: column,
        } as ColumnData;
      });
    });
  }, [data]);

  const tableFooterRow = useMemo(() => {
    return [
      { type: "TEXT", value: "合計" },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.unique_user as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.conversion_user as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.conversion_user_rate as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.lp_drop_rate as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.lp_scroll_rate as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.lp_average_stay_time as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.form_drop_rate as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.mobile as string,
      },
      {
        type: "TEXT",
        value: reportAgeRange.aggregated_report.tablet as string,
      },
      { type: "TEXT", value: reportAgeRange.aggregated_report.pc as string },
    ] as ColumnData[];
  }, [reportAgeRange]);

  const csvFooterRow = useMemo(() => {
    return [
      "合計",
      reportAgeRange.aggregated_report.unique_user.replaceAll(",", ""),
      reportAgeRange.aggregated_report.conversion_user.replaceAll(",", ""),
      reportAgeRange.aggregated_report.conversion_user_rate,
      reportAgeRange.aggregated_report.lp_drop_rate,
      reportAgeRange.aggregated_report.lp_scroll_rate,
      reportAgeRange.aggregated_report.lp_average_stay_time,
      reportAgeRange.aggregated_report.form_drop_rate,
      reportAgeRange.aggregated_report.mobile,
      reportAgeRange.aggregated_report.tablet,
      reportAgeRange.aggregated_report.pc,
    ].join(",");
  }, [reportAgeRange]);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px" mt={6}>
        <RowHeightSettingButton
          formValues={{ rowHeight }}
          onSubmit={handleSubmitRowHeightSetting}
        />
        <DisableColumnSelectButton
          formValues={{ disableColumns }}
          onSubmit={handleSubmitDisalbeColumnSelect}
          options={disableColumnOptions}
        />
        <CSVDownloadLink
          name={"年齢別レポート"}
          data={reportAgeRange.summary_report + csvFooterRow}
          filteredColumns={disableColumns}
        />
      </Flex>
      <Table>
        <ReportTableHeaderRow
          disableColumns={disableColumns}
          onSortClick={onSortClick}
          headerOptions={headerOptionsForAgeRangeReport}
        />
        {tableRows.map((row, i) => (
          <ReportTableDataRow
            key={i}
            row={row}
            disableColumns={disableColumns}
          />
        ))}
        <ReportTableDataRow
          disableColumns={disableColumns}
          row={tableFooterRow}
          rowHeight={`${rowHeight}px`}
        />
      </Table>
    </>
  );
};
